import React from 'react';
import { Button } from '@/ui/atoms/Button';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore, useAppStore } from '@/shared/infrastructure/stores';
import { Icon } from '@/ui/atoms/Icons';
import Tooltip from '@/ui/atoms/Tooltip/Tooltip';

const NAVIGATION_ITEMS = [
  { name: 'Manifiesto courier', href: '/manifiestos', icon: <Icon name="Package" size={20} /> },
  { name: 'Guía courier', href: '/guias', icon: <Icon name="FileText" size={20} /> },
];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuthStore();
  const { sidebarOpen, toggleSidebar } = useAppStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const clearFilters = (prefix: string) => {
    const filterKeys = [
      `${prefix}_filters`,
      `${prefix}_filters_backup`,
      `${prefix}_temp_filters`,
      `${prefix}_temp_filters_backup`,
      `${prefix}_hasUserSearched`,
      `${prefix}_hasUserSearched_backup`,
      `${prefix}_showFilters`,
      `${prefix}_showFilters_backup`,
      `${prefix}_showAdvancedFilters`,
      `${prefix}_showAdvancedFilters_backup`,
    ];
    
    filterKeys.forEach(key => sessionStorage.removeItem(key));
  };

  const handleNavigationClick = (href: string) => {
    if (href === '/manifiestos') clearFilters('manifests');
    if (href === '/guias') clearFilters('guides');
  };

  const isItemActive = (itemHref: string): boolean => {
    const numeroAceptacion = searchParams.get('numeroAceptacion');
    if (location.pathname === '/guias' && numeroAceptacion) {
      return itemHref === '/manifiestos';
    }
    return location.pathname === itemHref;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-lg border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Header con logo gubernamental */}
        <div className="bg-white flex flex-col items-center justify-center py-4 border-b border-gray-200">
          <div className="mb-3">
            <img 
              src="/logo.png" 
              alt="Gobierno de Chile - Aduana de Chile" 
              className="h-12 w-auto"
            />
          </div>
          {/* Barra decorativa */}
          <div className="w-full max-w-[200px] h-1.5 flex rounded-full overflow-hidden shadow-sm">
            <div className="bg-[#0477BF] flex-[0.3]"></div>
            <div className="bg-[#F23D4C] flex-[0.7]"></div>
          </div>
        </div>
        
        {/* Sección Empresas Courier */}
        <div className="px-4 py-4">
          <h2 className="text-gray-900 text-sm font-semibold mb-4">Empresas Courier</h2>
          <nav className="space-y-1">
            {NAVIGATION_ITEMS.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => handleNavigationClick(item.href)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isItemActive(item.href)
                    ? 'bg-[#0477BF] text-white'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.name}
              </Link>
            ))}
            <br />
            <hr className="border-gray-200 my-4" />
            <Link
                to="#"
                onClick={logout}
                className={`flex mt-4 items-center px-3 py-2 text-sm font-medium rounded-md transition-colors text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className="mr-3 hover:text-[#F23D4C]"><Icon color="#F23D4C" name="LogOut" size={20} /></span>
                Cerrar Sesión
              </Link>
          </nav>
        </div>
        
        {/* Cerrar Sesión */}
        <div className="absolute bottom-0 left-0 right-0 p-4  border-gray-200">
          {/*<button
            onClick={logout}
            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            <span className="truncate">Cerrar Sesión</span>
            <div className="w-2 h-2 bg-[#F23D4C] rounded-full flex-shrink-0"></div>
          </button>*/}
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-72' : ''}`}>
        <header className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSidebar}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <Icon name="Menu" size={20} />
              </Button>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {NAVIGATION_ITEMS.find(item => item.href === location.pathname)?.name || 'Portal Aduana Chile'}
              </h2>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <span className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                BIENVENIDO, {user?.name}
              </span>
              <Tooltip text="Cerrar sesión" position="bottom" className="bg-[#0477BF]">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-[#F23D4C] p-2"
                >
                  <Icon name="LogOut" size={18} />
                </Button>
              </Tooltip>
            </div>
          </div>
        </header>

        <main className="bg-gray-50 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
